// ═══════════════════════════════════════
// AUTOSAVE — Sauvegarde automatique de la partition
// ═══════════════════════════════════════
//
// Usage :
//   initAutosave()          — à appeler au DOMContentLoaded (après initFromServerData)
//   markDirty()             — à appeler sur chaque modification du contenu
//   markClean()             — appelé automatiquement par savePartition() au succès
//
// Comportement :
//   - Aucune sauvegarde si la partition n'est pas modifiée (_dirty = false)
//   - Aucune sauvegarde si l'utilisateur n'est pas propriétaire / pas connecté
//   - Timer de AUTOSAVE_INTERVAL ms réarmé à chaque markDirty()
//   - Un seul appel concurrentiel max (_saving guard)
//   - Indicateur visuel discret dans #save-status
//   - Confirmation beforeunload si modifications non sauvegardées

import { savePartition, showSaveStatus } from './navigation.js';
import { sessionToken, currentPartitionMeta } from './state.js';
import { t } from './i18n.js';

const AUTOSAVE_INTERVAL = 30_000; // 30 secondes

let _dirty  = false;
let _saving = false;
let _timer  = null;

// ── API publique ──────────────────────────────────────────────────────────────

export function initAutosave() {
    window.addEventListener('beforeunload', (e) => {
        if (!_dirty) return;
        e.preventDefault();
        // returnValue requis pour la compatibilité navigateurs (Chrome ignore le texte)
        e.returnValue = '';
    });
}

/** Appelé à chaque modification du contenu de l'éditeur. */
export function markDirty() {
    _dirty = true;
    _arm();
}

/** Appelé par savePartition() lorsque la sauvegarde manuelle/auto réussit. */
export function markClean() {
    _dirty = false;
    clearTimeout(_timer);
    _timer = null;
}

// ── Internes ──────────────────────────────────────────────────────────────────

function _arm() {
    clearTimeout(_timer);
    _timer = setTimeout(_tryAutosave, AUTOSAVE_INTERVAL);
}

async function _tryAutosave() {
    _timer = null;
    if (!_dirty)                                    return;
    if (_saving)                                    return;
    if (!sessionToken)                              return;
    if (!currentPartitionMeta?.is_owner)            return;

    _saving = true;
    _showAutosaveIndicator();

    try {
        await savePartition({ silent: true });
        // markClean() est appelé par savePartition() en cas de succès
    } catch (_) {
        // Échec silencieux — l'utilisateur peut sauvegarder manuellement
    } finally {
        _saving = false;
    }
}

function _showAutosaveIndicator() {
    const el = document.getElementById('save-status');
    if (!el) return;
    el.textContent = t('nav.autosave_saving');
    el.className   = 'save-status autosave';
}